import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { shareReplay, catchError } from 'rxjs/operators';

/**
 * The current user's effective permissions.
 *
 * `GET /personal/permissions` has existed on the API since the permission system was built but was
 * never consumed — the frontend has only ever gated on the `base.role` JWT claim. That is enough for
 * "is this person CM staff", but not for "may this particular user manage their company's API keys",
 * because permissions can be granted to a sub-role held by only some users of a company.
 *
 * Not to be confused with `PermissionsService`, which is admin CRUD over order field permissions.
 */
@Injectable({ providedIn: 'root' })
export class MyPermissionsService {
  private permissions$ = new BehaviorSubject<string[] | null>(null);
  private inFlight?: Observable<string[]>;

  constructor(private http: HttpClient) {}

  /**
   * Loads and caches the permission list. Safe to call repeatedly: concurrent callers share one
   * request, and once loaded it is served from memory.
   */
  load(): Observable<string[]> {
    if (this.permissions$.value) {
      return of(this.permissions$.value);
    }

    if (!this.inFlight) {
      this.inFlight = this.http.get<string[]>('/personal/permissions').pipe(
        // A failure must not blank the UI for everything else. An empty list simply means nothing
        // permission-gated renders, which is the safe direction to fail.
        catchError(() => of([])),
        tap((permissions) => {
          this.permissions$.next(permissions ?? []);
          this.inFlight = undefined;
        }),
        shareReplay(1)
      );
    }

    return this.inFlight;
  }

  /** Emits whenever the permission list changes, so the directive can re-render. */
  changes(): Observable<string[] | null> {
    return this.permissions$.asObservable();
  }

  has(permission: string): boolean {
    return (this.permissions$.value ?? []).includes(permission);
  }

  /**
   * Drops the cache. Must be called on logout and on company switch: switching company changes the
   * caller's scope, so a stale list could show a user controls they no longer have.
   */
  clear(): void {
    this.permissions$.next(null);
    this.inFlight = undefined;
  }
}
